/**
 * Serviço para Conversão Automática de Comunicações em Processos
 * Integra com IA para criar processos inteligentemente
 */

import { FirebaseService } from './firebaseService';
import AIAnalysisService from './aiAnalysisService';

class ProcessConversionService {
  
  /**
   * Converter comunicação em processo automaticamente
   */
  async convertComunicacaoToProcesso(userId, comunicacao) {
    try {
      console.log('🔄 Convertendo comunicação em processo:', comunicacao.id);

      // 1. Analisar comunicação com IA
      const analysis = await AIAnalysisService.analyzeComunicacao(comunicacao);
      
      // 2. Verificar se processo já existe
      const numeroProcesso = analysis.numeroProcesso;
      if (!numeroProcesso) {
        throw new Error('Número do processo não encontrado na comunicação');
      }

      const existingProcesso = await this.findExistingProcesso(userId, numeroProcesso);
      
      if (existingProcesso) {
        // 3a. Processo existe - apenas vincular comunicação
        console.log('📋 Processo já existe, vinculando comunicação:', numeroProcesso);
        await this.linkComunicacaoToProcesso(userId, comunicacao, existingProcesso, analysis);
        return existingProcesso;
      } else {
        // 3b. Processo não existe - criar novo
        console.log('🆕 Criando novo processo:', numeroProcesso);
        const novoProcesso = await this.createProcessoFromComunicacao(userId, comunicacao, analysis);
        await this.linkComunicacaoToProcesso(userId, comunicacao, novoProcesso, analysis);
        return novoProcesso;
      }
    } catch (error) {
      console.error('❌ Erro na conversão de comunicação para processo:', error);
      throw error;
    }
  }

  /**
   * Buscar processo existente pelo número
   */
  async findExistingProcesso(userId, numeroProcesso) {
    try {
      const processos = await FirebaseService.getUserProcessos(userId);
      return processos.find(p => p.numero === numeroProcesso);
    } catch (error) {
      console.error('❌ Erro ao buscar processo existente:', error);
      return null;
    }
  }

  /**
   * Criar processo a partir da comunicação e análise de IA
   */
  async createProcessoFromComunicacao(userId, comunicacao, analysis) {
    try {
      // Extrair informações para o processo
      const processoData = {
        numero: analysis.numeroProcesso,
        cliente: this.extractClienteFromComunicacao(comunicacao, analysis),
        tipo: this.mapTipoAtoToTipoProcesso(analysis.tipoAto),
        status: analysis.temPrazo ? 'Em andamento' : 'Aguardando',
        proximoPrazo: this.getProximoPrazo(analysis.prazos),
        oabResponsavel: this.extractOABFromComunicacao(comunicacao),
        
        // Dados da IA
        urgencia: analysis.urgencia,
        tipoAto: analysis.tipoAto,
        resumoIA: analysis.resumo,
        acoesSugeridas: analysis.acoesSugeridas,
        tags: analysis.tags,
        
        // Metadados
        criadoAutomaticamente: true,
        fonteComunicacao: comunicacao.id,
        tribunal: `${comunicacao.siglaTribunal} - ${comunicacao.nomeOrgao}`,
        dataUltimaComunicacao: comunicacao.data_disponibilizacao,
        
        // Status padrão
        concluido: false
      };

      const processo = await FirebaseService.addUserProcesso(userId, processoData);
      console.log('✅ Processo criado automaticamente:', processo.id);
      
      return processo;
    } catch (error) {
      console.error('❌ Erro ao criar processo:', error);
      throw error;
    }
  }

  /**
   * Vincular comunicação ao processo
   */
  async linkComunicacaoToProcesso(userId, comunicacao, processo, analysis) {
    try {
      // Atualizar comunicação com dados do processo e IA
      await FirebaseService.updateComunicacao(userId, comunicacao.id, {
        processoId: processo.id,
        numeroProcesso: processo.numero,
        
        // Dados da análise de IA
        aiAnalysis: analysis,
        temPrazo: analysis.temPrazo,
        urgencia: analysis.urgencia,
        acoesSugeridas: analysis.acoesSugeridas,
        tags: analysis.tags,
        
        // Status
        processada: true,
        processadaEm: new Date().toISOString()
      });

      // Atualizar processo com dados da nova comunicação
      await FirebaseService.updateUserProcesso(userId, processo.id, {
        dataUltimaComunicacao: comunicacao.data_disponibilizacao,
        totalComunicacoes: (processo.totalComunicacoes || 0) + 1,
        
        // Atualizar urgência se necessário
        urgencia: this.updateUrgencia(processo.urgencia, analysis.urgencia),
        
        // Atualizar próximo prazo se necessário
        proximoPrazo: this.updateProximoPrazo(processo.proximoPrazo, analysis.prazos),
        
        // Merge de tags
        tags: this.mergeTags(processo.tags || [], analysis.tags),
        
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Comunicação vinculada ao processo:', processo.numero);
    } catch (error) {
      console.error('❌ Erro ao vincular comunicação ao processo:', error);
      throw error;
    }
  }

  /**
   * Extrair cliente da comunicação
   */
  extractClienteFromComunicacao(comunicacao, analysis) {
    // Tentar extrair do texto com IA ou usar padrões
    if (analysis.resumo && analysis.resumo.includes('parte')) {
      return 'Cliente (extraído da comunicação)';
    }
    
    // Usar tribunal como fallback
    return `Processo ${comunicacao.siglaTribunal}`;
  }

  /**
   * Mapear tipo de ato para tipo de processo
   */
  mapTipoAtoToTipoProcesso(tipoAto) {
    const mapeamento = {
      'citação': 'Cível',
      'intimação': 'Cível',
      'sentença': 'Cível',
      'despacho': 'Cível',
      'decisão': 'Cível',
      'trabalhista': 'Trabalhista',
      'criminal': 'Criminal',
      'tributário': 'Tributário'
    };

    return mapeamento[tipoAto?.toLowerCase()] || 'Cível';
  }

  /**
   * Obter próximo prazo dos prazos detectados
   */
  getProximoPrazo(prazos) {
    if (!prazos || prazos.length === 0) return null;
    
    // Pegar o prazo mais próximo
    const prazosOrdenados = prazos.sort((a, b) => new Date(a.dataLimite) - new Date(b.dataLimite));
    return prazosOrdenados[0].dataLimite;
  }

  /**
   * Extrair OAB da comunicação
   */
  extractOABFromComunicacao(comunicacao) {
    // Usar OAB da consulta se disponível
    if (comunicacao.oabNumero && comunicacao.oabSeccional) {
      return `${comunicacao.oabNumero}/${comunicacao.oabSeccional}`;
    }
    
    return 'N/A';
  }

  /**
   * Atualizar urgência (pegar a maior)
   */
  updateUrgencia(urgenciaAtual, novaUrgencia) {
    const niveis = { 'alta': 3, 'media': 2, 'baixa': 1 };
    const atual = niveis[urgenciaAtual] || 1;
    const nova = niveis[novaUrgencia] || 1;
    
    const resultado = Math.max(atual, nova);
    return Object.keys(niveis).find(key => niveis[key] === resultado);
  }

  /**
   * Atualizar próximo prazo (pegar o mais próximo)
   */
  updateProximoPrazo(prazoAtual, novosPrazos) {
    if (!novosPrazos || novosPrazos.length === 0) return prazoAtual;
    
    const proximoNovoPrazo = this.getProximoPrazo(novosPrazos);
    if (!proximoNovoPrazo) return prazoAtual;
    
    if (!prazoAtual) return proximoNovoPrazo;
    
    // Retornar o mais próximo
    return new Date(proximoNovoPrazo) < new Date(prazoAtual) ? proximoNovoPrazo : prazoAtual;
  }

  /**
   * Fazer merge de tags
   */
  mergeTags(tagsAtuais, novasTags) {
    const todasTags = [...tagsAtuais, ...novasTags];
    return [...new Set(todasTags)]; // Remover duplicatas
  }

  /**
   * Processar múltiplas comunicações em lote
   */
  async processMultipleComunicacoes(userId, comunicacoes, onProgress) {
    const results = [];
    
    for (let i = 0; i < comunicacoes.length; i++) {
      try {
        const processo = await this.convertComunicacaoToProcesso(userId, comunicacoes[i]);
        results.push({
          comunicacaoId: comunicacoes[i].id,
          processoId: processo.id,
          numeroProcesso: processo.numero,
          success: true
        });
        
        if (onProgress) {
          onProgress(i + 1, comunicacoes.length);
        }
        
        // Delay para evitar sobrecarga
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`❌ Erro ao processar comunicação ${comunicacoes[i].id}:`, error);
        results.push({
          comunicacaoId: comunicacoes[i].id,
          error: error.message,
          success: false
        });
      }
    }
    
    return results;
  }

  /**
   * Marcar processo como concluído via comunicação
   */
  async markProcessoAsConcluido(userId, comunicacao) {
    try {
      if (!comunicacao.processoId) {
        throw new Error('Comunicação não está vinculada a um processo');
      }

      // Marcar processo como concluído
      await FirebaseService.updateUserProcesso(userId, comunicacao.processoId, {
        concluido: true,
        dataConclusao: new Date().toISOString(),
        status: 'Concluído',
        concluidoViaComunicacao: comunicacao.id
      });

      // Marcar comunicação como concluída
      await FirebaseService.updateComunicacao(userId, comunicacao.id, {
        isRead: true,
        processoConcluidoEm: new Date().toISOString()
      });

      console.log('✅ Processo marcado como concluído via comunicação:', comunicacao.processoId);
      return true;
    } catch (error) {
      console.error('❌ Erro ao marcar processo como concluído:', error);
      throw error;
    }
  }
}

export default new ProcessConversionService();
